import React, { useEffect, useState, lazy, Suspense } from "react";
import './AppointmentPage.css'
import AppointmenttypeV2 from "../Dashboard/AppointmenttypeV2";
import axios from "axios";
import { spacerData } from "../Dashboard/CommonApiCallCalender";
import LoadingSpinner from "../Common/LoadingSpinner";
import { useNavigate } from "react-router-dom";

// Perf: Hbooking (health-journey sub-flow, only entered from currentStep 2)
// pulls in ClinicBookingV2/PreferredTimeV2/SecondStep as static imports. Those
// are already lazy-loaded elsewhere for the main GP journey, so eagerly
// importing Hbooking here duplicated all of that weight into the widget's
// critical-path bundle. Lazy-loading it lets webpack split it into its own
// chunk (deduped against the existing lazy imports) without changing when or
// how it renders.
const Hbooking = lazy(() => import("../Healthjourney/Hbooking"));

// ── DF-03/04: sessionStorage cache helpers (5-minute TTL) ────────────────────
const CACHE_KEY_HEALTH = 'ldc_w_health_v1';
const CACHE_KEY_CATS = 'ldc_w_cats_v1';
const CACHE_TTL_MS = 5 * 60 * 1000;

function readCache(key) {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts > CACHE_TTL_MS) { sessionStorage.removeItem(key); return null; }
        return data;
    } catch { return null; }
}

function writeCache(key, data) {
    try { sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch { }
}

// ── UX-01: Shimmer skeleton — mirrors the apptTypeBox card structure ──────────
const CategorySkeleton = () => {
    const shimmer = {
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'ldc-shimmer 1.4s infinite',
        borderRadius: 4,
    };
    return (
        <div style={{ maxWidth: 605, margin: 'auto' }}>
            <style>{`
                @keyframes ldc-shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>
            {/* Header banner skeleton */}
            <div style={{ background: '#00838540', padding: 10, marginTop: 50, marginBottom: 4 }}>
                <div style={{ ...shimmer, height: 24, width: '40%', margin: 'auto' }} />
            </div>
            {/* 5 card skeletons */}
            {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} style={{
                    background: '#fff',
                    borderTop: '1px solid #ddd',
                    borderBottom: '1px solid #ddd',
                    paddingTop: 12,
                    paddingBottom: 12,
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '10%',
                    marginTop: 2,
                }}>
                    <div style={{ ...shimmer, width: 48, height: 48, borderRadius: 8, marginRight: 16, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ ...shimmer, height: 16, width: '55%', marginBottom: 6 }} />
                        <div style={{ ...shimmer, height: 12, width: '35%' }} />
                    </div>
                    <div style={{ ...shimmer, width: 16, height: 16, borderRadius: 3, marginRight: '5%' }} />
                </div>
            ))}
        </div>
    );
};

const UnifiedAppointmentType = ({ onNext, onBack, onSubFlowEnter, onSubFlowExit, productCategoryId, productId, clinicId, hashCode, partnerId, languageData, clinicData, hideAdditionalPreferences }) => {
    const navigate = useNavigate();
    const currentCacheKeyHealth = `ldc_w_health_v1_${partnerId || ''}_${hashCode || ''}`;
    const currentCacheKeyCats = `ldc_w_cats_v1_${partnerId || ''}_${hashCode || ''}`;
    const [currentStep, setCurrentStep] = useState(1);
    const [getAllHealthScreenData, setGetAllHealthScreenData] = useState(null);
    const [sendingData, setsendingData] = useState({
        non_gp_product_id: "",
        productCategory: { title: "Health Screen" },
        isback: onBack
    });
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState(null);

    const callbackAppointment = (data, formType) => {
        const sanitizedData = {
            ...data,
            productCategory: { ...data.productCategory, icon: undefined }
        };

        if (data.productCategory.type === "gp" || data.productCategory.type === "video") {
            onNext();
        } else {
            onSubFlowEnter?.();  // hide AppointmentPage's bar; Hbooking will show its own
            setCurrentStep(2);
            var a = data;
            a.isback = () => {
                onSubFlowExit?.(); // restore AppointmentPage's bar if user goes back
                setCurrentStep(1);
                console.log("Back button clicked for health journey");
            };
            setsendingData(a);
        }
    };
    const categoryFilterFromPartner = (categories) => {
        return categories.filter((category) => {
            const catId = category.product_category_id;

            // If no partners, category is never excluded
            if (!Array.isArray(category.partners) || category.partners.length === 0) {
                return true;
            }

            // Check every partner's category_customization for an exclusion matching this category
            const isExcluded = category.partners.some((partner) =>
                Array.isArray(partner.category_customization) &&
                partner.category_customization.some(
                    (customization) =>
                        customization.product_category_id === catId &&
                        customization.exclude_category === true
                )
            );

            return !isExcluded;
        });
    };
    // ── DF-05: fetch (optionally in background for staleWhileRevalidate) ──────
    const fetchAndUpdate = async (isBackground) => {
        if (!isBackground) {
            setLoading(true);
            setErrorMsg(null);
        }
        try {
            // ── Partner mode: call corporate API by partnerId ─────────────────
            if (partnerId) {
                // Fetch partner data + product categories in parallel to resolve icons
                const [partnerRes, optionsRes] = await Promise.all([
                    spacerData(`get-corporate-customers/${partnerId}`, null, null, false),
                    fetch(`${process.env.REACT_APP_BASE_URLOPEN}product-category/page/1`)
                ]);

                const responseData = partnerRes?.response ? partnerRes.response.data : partnerRes?.data;
                if (responseData && responseData.success === false) {
                    setErrorMsg("Partner not found");
                    setLoading(false);
                    return;
                }

                const partnerData = partnerRes?.data?.data || {};
                const corporateProducts = (partnerData.corporate_products || [])
                    .filter(p => p.status == 1 || p.status === true);

                // Map corporate_products to the same shape as non-gp-products
                const healthScreenData = corporateProducts.map(p => ({
                    ...p,
                    product_category_id: p.product_category_id ?? 2,
                    product_category_type: p.product_category_type ?? p.product_category_id ?? 2,
                }));
                // console.log("healthScreenData", healthScreenData)
                const formattedData = {
                    getAllHealthScreenData: {
                        getAllHealthScreenData: { data: healthScreenData },
                        allGPproduct: []
                    },
                    ContinueButoonStylingMiddle: true
                };

                // Build icon + name lookup map: product_category_id -> icon_name / name
                let iconLookup = {};
                let nameLookup = {};
                try {
                    const optionsData = await optionsRes.json();
                    const apiCategories = optionsData?.data?.data || [];
                    apiCategories.forEach((cat) => {
                        iconLookup[cat.product_category_id] = cat.icon_name || "HeartOutlined";
                        nameLookup[cat.product_category_id] = cat.name || "Health Screens";
                    });
                } catch { /* ignore — fall back to HeartOutlined */ }

                // ── category_customization: build one tile per entry ────────────
                // Falls back to single category from product_category_label when array absent
                const categoryCustomization = partnerData.category_customization;
                let partnerCategoryOptions;

                if (Array.isArray(categoryCustomization) && categoryCustomization.length > 0) {
                    partnerCategoryOptions = categoryCustomization
                        // Only show categories that have at least one active product
                        .filter((cat) =>
                            healthScreenData.some(
                                (p) =>
                                    p.product_category_id == cat.product_category_id ||
                                    p.product_category_type == cat.product_category_id
                            )
                        )
                        .map((cat) => ({
                            key: `cat-${cat.product_category_id}`,
                            title: cat.change_category_label
                                ? cat.product_category_label
                                : (nameLookup[cat.product_category_id] || cat.name || "Health Screens"),
                            description: cat.product_category_description || "",
                            product_category_id: cat.product_category_id,
                            type: "d2c",
                            // Match real icon by category id; fall back to HeartOutlined
                            icon_name: iconLookup[cat.product_category_id] || "HeartOutlined",
                        }));
                } else {
                    // Legacy fallback: single category
                    const categoryLabel = partnerData.product_category_label || "Health Screens";
                    const catId = healthScreenData[0]?.product_category_id ?? 2;
                    partnerCategoryOptions = [{
                        key: `cat-${catId}`,
                        title: categoryLabel,
                        product_category_id: catId,
                        type: "d2c",
                        icon_name: iconLookup[catId] || "HeartOutlined",
                        description: "",
                    }];
                }

                // Write to sessionStorage cache
                writeCache(currentCacheKeyHealth, formattedData);
                writeCache(currentCacheKeyCats, partnerCategoryOptions);

                setGetAllHealthScreenData(formattedData);
                setOptions(partnerCategoryOptions);
                return;
            }

            // ── Standard D2C / Corporate (hashCode) mode ──────────────────────
            const [healthScreenRes, optionsRes, corporateRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BASE_URLOPEN}non-gp-products`, {
                    headers: { "Content-Type": "application/json" },
                    params: {
                        category: "clinician_type",
                        product_category_types: hashCode ? ['corporate'] : ['d2c'],
                        ...(hashCode && { hashCode: hashCode }),
                    }
                }),
                fetch(`${process.env.REACT_APP_BASE_URLOPEN}product-category/page/1`),
                hashCode ? spacerData(`get-corporate-customers/${hashCode}`, null, null, false) : Promise.resolve(null)
            ]);

            if (hashCode && corporateRes) {
                const corporateDataObj = corporateRes?.response ? corporateRes.response.data : corporateRes?.data;
                if (corporateDataObj && corporateDataObj.success === false) {
                    setErrorMsg("Corporate not found");
                    setLoading(false);
                    return;
                }
            }

            const healthScreenData = healthScreenRes.data.data || [];
            const formattedData = {
                getAllHealthScreenData: {
                    getAllHealthScreenData: { data: healthScreenData },
                    allGPproduct: []
                },
                ContinueButoonStylingMiddle: true
            };

            const optionsData = await optionsRes.json();
            const categories = optionsData.data.data;
            // console.log("categories (raw)", categories);
            const filteredCategoryFromPartners = categoryFilterFromPartner(categories);
            // console.log("categories (after partner exclusion filter)", filteredCategoryFromPartners);
            const dynamicOptions = filteredCategoryFromPartners.map((cat) => ({
                key: `cat-${cat.product_category_id}`,
                title: cat.name,
                product_category_id: cat.product_category_id,
                type: cat.type,
                icon_name: cat.icon_name,
                description: cat.description,
            }));

            // ── DF-03/04: Write to sessionStorage cache ───────────────────────
            writeCache(currentCacheKeyHealth, formattedData);
            writeCache(currentCacheKeyCats, dynamicOptions);

            setGetAllHealthScreenData(formattedData);
            setOptions(dynamicOptions);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            if (!isBackground) setLoading(false);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);

        // Partner mode: always fetch fresh — never serve stale full-category cache
        if (partnerId) {
            fetchAndUpdate(false);
            return;
        }

        // ── DF-03/04: Try cache first ───────────────────────────────────
        const cachedHealth = readCache(currentCacheKeyHealth);
        const cachedCats = readCache(currentCacheKeyCats);

        if (cachedHealth && cachedCats) {
            // UX-04: Instant render from cache — no skeleton shown
            setGetAllHealthScreenData(cachedHealth);
            setOptions(cachedCats);
            setLoading(false);
            // DF-05: Silently refresh in background
            fetchAndUpdate(true);
            return;
        }

        // Cache cold → show skeleton, fetch, then render
        fetchAndUpdate(false);
    }, [hashCode, partnerId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Perf: warm the lazy Hbooking chunk after idle, so it's already cached by
    // the time currentStep reaches 2 — mirrors AppointmentPageV2's prefetchStepChunks.
    useEffect(() => {
        const prefetchHbooking = () => { import("../Healthjourney/Hbooking"); };
        if ("requestIdleCallback" in window) {
            window.requestIdleCallback(prefetchHbooking);
        } else {
            setTimeout(prefetchHbooking, 2000);
        }
    }, []);

    // ── UX-01: Show shimmer skeleton while loading (replaces height:100vh Spin)
    if (loading) {
        return (
            <div className="page-wrapper">
                <main className="main">
                    <CategorySkeleton />
                </main>
            </div>
        );
    }

    if (errorMsg) {
        return (
            <div className="page-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', padding: '20px' }}>
                <style>{`
                    @keyframes cardFadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
                <div style={{
                    background: '#fff',
                    padding: '40px 30px',
                    borderRadius: '16px',
                    boxShadow: '0 20px 40px -15px rgba(0, 131, 133, 0.12)',
                    textAlign: 'center',
                    maxWidth: '420px',
                    width: '90%',
                    border: '1px solid #eaeaea',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    animation: 'cardFadeIn 0.4s ease-out'
                }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#e05e5e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '20px', filter: 'drop-shadow(0 4px 6px rgba(224, 94, 94, 0.2))' }}>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <h2 style={{ margin: '0 0 10px 0', color: '#2c3e50', fontSize: '20px', fontWeight: '600', letterSpacing: '-0.3px' }}>
                        {errorMsg}
                    </h2>
                    <p style={{ margin: 0, color: '#7f8c8d', fontSize: '14px', lineHeight: '1.5' }}>
                        The requested page could not be loaded because the {partnerId ? "partner" : "corporate"} account is invalid or does not exist.
                    </p>
                    <button
                        onClick={() => navigate("/")}
                        style={{
                            marginTop: '24px',
                            background: '#008385',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease, transform 0.1s ease',
                            outline: 'none',
                            boxShadow: '0 4px 10px rgba(0, 131, 133, 0.2)'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#008385'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#008385'}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        Return Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <main className="main">
                {currentStep === 1 && getAllHealthScreenData && options.length > 0 && (
                    <AppointmenttypeV2
                        {...getAllHealthScreenData}
                        callback={callbackAppointment}
                        callBackForbackpage={() => console.log("Back button clicked")}
                        options={options}
                        productCategoryId={productCategoryId}
                        productId={productId}
                        hashCode={hashCode}
                        partnerId={partnerId}
                    />
                )}
                {currentStep === 2 && (
                    <Suspense fallback={<LoadingSpinner />}>
                        <Hbooking data={sendingData} languageData={languageData} clinicId={clinicId} clinicData={clinicData} hideAdditionalPreferences={hideAdditionalPreferences} />
                    </Suspense>
                )}
            </main>
        </div>
    );
};

export default UnifiedAppointmentType;